import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  input,
  inject,
  ChangeDetectionStrategy,
  signal,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  output,
  viewChild,
} from '@angular/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import {
  ReplaySubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  fromEvent,
  skip,
  takeUntil,
} from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { delayFor } from 'app/core/utilities/async-utils';
import { SnackBarComponent, SnackBarData, SnackBarType } from 'app/shared/snackbar/snackbar.component';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import BadgeQuestions from 'entity/BadgeQuestions';
import Social from 'entity/Social';
import User from 'entity/User';
import { BadgeDataValue, BaseUser } from 'messages/websocket.messages';

import { BadgingService } from '../services/badging/badging.service';
import { ChatService } from '../services/chat.service';
import { FAVORITE_INITIAL_STATE, FavoriteResponse, FavoriteService } from '../services/favorite.service';
import { PresentationService } from '../services/presentation/presentation.service';
import { QAService } from '../services/qa.service';
import { SceneService } from '../services/scene.service';
import { TeleportService } from '../services/teleport.service';
import { socialAppActions, socialAppSelectors } from '../state/app';
import { VideoChatService } from '../services/videochat.service';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'app/shared/shared.module';
import { CoreModule } from 'app/core/core.module';
import { CommonsModule } from 'app/commons/commons.module';
import { MatIconModule } from '@angular/material/icon';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { HelpCenterUrls } from 'app/utils/help-center';

const MAIN_QUESTIONS = ['Name', 'First Name', 'Last Name', 'Title', 'Company', 'LinkedIn', 'About Me'];

const MAX_ABOUT_ME_HEIGHT = 90;
const DEFAULT_X_POSITION = -170;
const DEFAULT_Y_POSITION = -135;
const MAX_FEATURE_CHIP_LENGTH = 43;

export interface PreviewUserInfo {
  userName?: string;
  companyName?: string;
  userTitle?: string;
  aboutMe?: string;
  linkedIn?: string;
}

@Component({
  selector: 'app-badging-info',
  templateUrl: './badging-info.component.html',
  styleUrls: ['./badging-info.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    CommonsModule,
    CoreModule,
    MatIconModule,
    DragDropModule,
    MatTooltipModule,
    MatTabsModule,
    MatSnackBarModule,
    MatButtonModule,
  ],

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BadgingInfoComponent extends Loggable implements OnDestroy, OnInit, AfterViewInit {
  private chatService = inject(ChatService);
  private badgingService = inject(BadgingService);
  private qaService = inject(QAService);
  private store = inject(Store);
  private sceneService = inject(SceneService);
  private teleportService = inject(TeleportService);
  private favoriteService = inject(FavoriteService);
  private elementRef = inject(ElementRef);
  private _snackBar = inject(MatSnackBar);
  private presentationService = inject(PresentationService);
  private videoChatService = inject(VideoChatService);

  // Inputs used to handle preview badge
  public readonly customStyle = input<boolean>();
  public readonly previewBadgeInfo = input<PreviewUserInfo>();
  public readonly isPreviewCard = input<boolean>(false);
  public readonly isOnboardingExperience = input<boolean>(false);
  public readonly isAssociatedContact = input<boolean>(false);
  public readonly userInput = input<User | undefined>(undefined); // Used for summary/preview

  //Inputs to handle summary badge
  public readonly isSummaryView = input<boolean>();
  public readonly summaryTimeline = input<boolean>();
  public readonly summaryTimelineBadge = input<BadgeQuestions[]>();
  public readonly summaryBadgeClosed = output<boolean>();

  private readonly aboutMeSection = viewChild<ElementRef<HTMLDivElement>>('aboutMeSection');

  // Signals for component state
  protected readonly socialSignal = signal<Social | undefined>(undefined);
  protected readonly mainPlayerIdSignal = signal<string | undefined>(undefined);
  protected readonly isHostSignal = signal<boolean>(false);
  protected readonly visualIndicatorQuestionSignal = signal<string | undefined>(undefined);
  // Keep badgeQuestionsSignal as it's a fundamental data source
  protected readonly badgeQuestionsSignal = signal<BadgeDataValue[] | undefined>(undefined);
  // Signals that will be replaced by computed
  protected readonly showBtnSignal = signal<boolean>(false);
  protected readonly userOpenOwnBadgeSignal = signal<boolean>(false);
  protected readonly isOnViewportSignal = signal<boolean>(false);
  protected readonly newVisualIndicatorsSignal = signal<boolean>(false);
  protected readonly ringColorSignal = signal<string | undefined>(undefined);
  protected readonly defaultAboutMeMessageSignal = signal<string>('No information has been provided by this person.');
  protected readonly blockActionMessageSignal = signal<string>('');

  // Newly converted/added signals
  protected readonly userSignal = signal<User | undefined>(undefined);
  protected readonly userIdSignal = signal<string | undefined>(undefined);
  protected readonly baseUserSignal = signal<BaseUser | undefined>(undefined);
  protected readonly isChatOpenSignal = signal<boolean>(false);
  protected readonly isBadgeChatSignal = signal<boolean>(false);
  protected readonly badgeCardWasOpenSignal = signal<boolean>(false);
  protected readonly isCrossRoomBadgeSignal = signal<boolean>(false);
  protected readonly blockActionButtonsSignal = signal<boolean>(false);
  protected readonly blockMapButtonSignal = signal<boolean>(false);
  protected readonly isOnPresentationSignal = signal<boolean>(false);
  protected readonly userReachableSignal = signal<boolean>(true);
  protected readonly findEngagedSignal = signal<boolean>(false);
  protected readonly teleportEngagedSignal = signal<boolean>(false);
  protected readonly chatEngagedSignal = signal<boolean>(false);
  protected readonly isUserPresentingToRoomSignal = signal<boolean>(false);
  protected readonly isUserPresentingToGroupSignal = signal<boolean>(false);
  protected readonly connectedRoomSetSignal = signal<Social[] | undefined>(undefined);
  protected readonly roomNameSignal = signal<string | undefined>(undefined);
  protected readonly roomIdSignal = signal<string | undefined>(undefined);
  protected readonly favoriteResponseSignal = signal<FavoriteResponse>(FAVORITE_INITIAL_STATE);

  private featureChipMaxLength = MAX_FEATURE_CHIP_LENGTH; // Constant, not a signal
  protected dragPosition = { x: DEFAULT_X_POSITION, y: DEFAULT_Y_POSITION }; // Managed by drag directive
  protected hoveringChild = false; // Seems fine as a direct property for now

  private destroyed$ = new ReplaySubject<boolean>(1);

  // Computed Signals
  protected readonly userInfoSignal = computed(() => {
    return this.userSignal() || this.userInput();
  });

  protected readonly playerSignal = computed(() => {
    const userId = this.userIdSignal();
    return userId ? this.sceneService.getPlayer(userId) : undefined;
  });

  protected readonly mainPlayerSignal = computed(() => {
    const mainPlayerId = this.mainPlayerIdSignal();
    return mainPlayerId ? this.sceneService.getPlayer(mainPlayerId) : undefined;
  });

  protected readonly isOwnBadgeSignal = computed(() => this.mainPlayerIdSignal() === this.userIdSignal());

  protected readonly favoriteList = toSignal(this.favoriteService.favoriteListStream$, {
    initialValue: [] as string[],
  });

  protected readonly isFavoriteSignal = computed(() => {
    return this.favoriteList().includes(this.userIdSignal() || '');
  });

  protected readonly featuredQuestionsSignal = computed(() => {
    const questions = this.badgeQuestionsSignal();
    if (questions) {
      return questions.filter(
        (question: BadgeDataValue) => MAIN_QUESTIONS.indexOf(question.propertyName) === -1 && question.featured,
      );
    }
    return [] as BadgeDataValue[];
  });

  protected readonly shouldShowTableRowSignal = computed(() => {
    return (
      this.tableQuestionsSignal().length > 0 &&
      this.tableQuestionsSignal().some((question: BadgeDataValue) => this.shouldShowQuestion(question))
    );
  });

  protected readonly shouldShowFeaturedRowSignal = computed(() => {
    return (
      this.featuredQuestionsSignal().length > 0 &&
      this.featuredQuestionsSignal().some((question: BadgeDataValue) => this.shouldShowQuestion(question))
    );
  });

  protected readonly tableQuestionsSignal = computed(() => {
    const questions = this.badgeQuestionsSignal();
    if (questions) {
      return questions.filter(
        (question: BadgeDataValue) => MAIN_QUESTIONS.indexOf(question.propertyName) === -1 && !question.featured,
      );
    }
    return [] as BadgeDataValue[];
  });

  // New computed signals to replace existing signals
  protected readonly userName = computed(() => {
    // Handle preview badge scenario
    if (this.isPreviewCard()) {
      return this.propertyHasValue('First Name') && this.propertyHasValue('Last Name')
        ? this.formatName(this.getQuestionProperty('First Name')?.value, this.getQuestionProperty('Last Name')?.value)
        : this.previewBadgeInfo()?.userName;
    } else if (this.isSummaryView() || this.summaryTimeline()) {
      // For summary, use the @Input() user for name if available, otherwise previewInfo as a fallback.
      return this.userInfoSignal()
        ? this.formatName(this.userInfoSignal()?.firstName ?? '', this.userInfoSignal()?.lastName ?? '')
        : (this.previewBadgeInfo()?.userName ?? '');
    }

    // For regular badge:
    const baseUser = this.baseUserSignal();
    const firstNameQ = this.getQuestionProperty('First Name')?.value;
    const lastNameQ = this.getQuestionProperty('Last Name')?.value;

    // Prioritize question data, then baseUser data for name parts
    const fName = firstNameQ ?? baseUser?.firstName;
    const lName = lastNameQ ?? baseUser?.lastName;

    return fName || lName ? this.formatName(fName, lName) : '';
  });

  protected readonly badgeSubtitle = computed(() => {
    const title = this.getBadgeTitle();
    const company = this.getCompanyName();

    if (title && company) {
      return `${title} @ ${company}`;
    }
    if (title) {
      return `${title}`;
    }
    return company ? `${company}` : '';
  });

  protected readonly badgeLinkedIn = computed(() => {
    if (this.isPreviewCard()) {
      return this.getQuestionProperty('LinkedIn')?.value ?? this.previewBadgeInfo()?.linkedIn;
    }
    return this.getQuestionProperty('LinkedIn')?.value;
  });

  protected readonly badgeAboutMe = computed(() => {
    if (this.isPreviewCard()) {
      return this.getQuestionProperty('About Me')?.value ?? this.previewBadgeInfo()?.aboutMe;
    }
    return this.getQuestionProperty('About Me')?.value;
  });

  protected readonly blockActionMessage = computed(() => {
    if (this.isOwnBadgeSignal()) {
      return '';
    }

    if (this.isOnPresentationSignal()) {
      return 'This feature is unavailable during a presentation';
    }

    if (this.isOnViewportSignal()) {
      return 'You are too close to teleport';
    }

    if (this.isCrossRoomBadgeSignal()) {
      const baseUser = this.baseUserSignal();
      if (!this.userReachableSignal()) {
        return `${this.formatName(
          baseUser?.firstName,
          baseUser?.lastName,
        )} is in ${this.checkRoomName()}. You can't join the room at the moment. Please try again later.`;
      } else {
        return `${this.formatName(
          baseUser?.firstName,
          baseUser?.lastName,
        )} is in ${this.checkRoomName()}. Change room on map dropdown to navigate to that room.`;
      }
    }

    return '';
  });

  protected readonly showEditIconSignal = computed(() => {
    return (
      !this.isPreviewCard() &&
      !this.isSummaryView() &&
      this.isOwnBadgeSignal() &&
      (this.shouldShowTableRowSignal() || this.shouldShowFeaturedRowSignal())
    );
  });

  protected readonly aboutMeShowButtonSignal = computed(() => {
    const aboutMeSection = this.aboutMeSection();
    if (!aboutMeSection || !aboutMeSection.nativeElement) {
      return false;
    }
    return aboutMeSection?.nativeElement?.clientHeight > MAX_ABOUT_ME_HEIGHT;
  });

  protected readonly shouldShowDetailsSectionSignal = computed(() => {
    return this.shouldShowTableRowSignal() || this.isHostSignal();
  });

  public constructor() {
    super();

    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((social) => {
        this.socialSignal.set(social);
      });

    this.store
      .select(coreSelectors.getUserId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((mainPlayerId) => {
        this.mainPlayerIdSignal.set(mainPlayerId);
      });

    this.store
      .select(socialAppSelectors.isHost)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHost) => {
        this.isHostSignal.set(isHost);
      });

    this.store
      .select(socialAppSelectors.getVisualIndicatorInfo)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((visualIndicatorInfo) => {
        if (visualIndicatorInfo.indicatorLocation) {
          this.visualIndicatorQuestionSignal.set(visualIndicatorInfo.indicatorLocation);
        }
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.visual_indicators)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => {
        this.newVisualIndicatorsSignal.set(enabled);
      });

    fromEvent(document, 'keydown')
      .pipe(
        takeUntil(this.destroyed$),
        filter((event) => (event as KeyboardEvent).key === 'Escape'),
      )
      .subscribe(() => {
        // If is preview card, should not have close behavior
        if (!this.isPreviewCard() && this.badgeCardWasOpenSignal() && !this.isSummaryView()) {
          this.closeBadge();
        }
      });

    fromEvent(document, 'click')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event) => {
        if (!this.isPreviewCard() && !this.isSummaryView()) {
          event.preventDefault();
          event.stopPropagation();
          const clickedInside = this.elementRef.nativeElement.contains(event.target);
          if (!clickedInside && this.badgeCardWasOpenSignal()) {
            this.closeBadge();
          }
        }
      });
  }

  // eslint-disable-next-line @angular-eslint/no-async-lifecycle-method
  public async ngAfterViewInit(): Promise<void> {
    this.store
      .select(socialAppSelectors.isChatEnabled)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((enabled) => {
        this.isChatOpenSignal.set(enabled);
      });

    await delayFor(1);
    this.badgeCardWasOpenSignal.set(true);

    const chatFeedEl = document.querySelector('app-social-chat-feed');
    if (chatFeedEl) {
      fromEvent(chatFeedEl, 'scroll')
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          //If user scroll chat feed, close badge if it is a chat badge
          if (this.isBadgeChatSignal()) {
            this.closeBadge();
          }
        });
    }
  }

  public ngOnInit(): void {
    if (this.isSummaryView() || this.summaryTimeline()) {
      const summaryTimelineBadge = this.summaryTimelineBadge();
      if (summaryTimelineBadge) {
        this.badgeQuestionsSignal.set(
          summaryTimelineBadge.map((item) => {
            return { propertyName: item.property ?? '', value: item.value ?? '' };
          }),
        );
      }
      if (this.userIdSignal() && this.userIdSignal() !== this.userInfoSignal()?.userId) {
        this.badgeCardWasOpenSignal.set(false);
      } else if (this.userIdSignal() === this.userInfoSignal()?.userId) {
        // If the badge is already open, we should not set it to false
        this.badgeCardWasOpenSignal.set(true);
      }
      this.userIdSignal.set(this.userInfoSignal()?.userId);
      return;
    }

    // If is preview card, the treatment below is not necessary
    if (this.isPreviewCard()) {
      this.badgingService.previewBadgeData$.pipe(takeUntil(this.destroyed$)).subscribe((previewBadgeData) => {
        this.badgeQuestionsSignal.set(previewBadgeData);
      });
      return;
    }

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.visual_indicators)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((newVisualIndicators) => {
        this.newVisualIndicatorsSignal.set(newVisualIndicators);
      });

    const observable = combineLatest({
      badgeUserId: this.badgingService.badgeUserIdOpen$,
      badgeData: this.badgingService.badgeData$,
      badgeUserRingColor: this.badgingService.badgeUserRingColor$,
    });

    observable
      .pipe(
        distinctUntilChanged((a, b) => _.isEqual(a, b)),
        takeUntil(this.destroyed$),
      )
      .subscribe(async (value) => {
        if (!value.badgeUserId || value.badgeData.length === 0) {
          return;
        }
        if (this.userIdSignal() === value.badgeUserId && _.isEqual(this.badgeQuestionsSignal(), value.badgeData)) {
          return;
        }
        this.resetDragPosition();
        this.isBadgeChatSignal.set(this.badgingService.isChatBadge());
        // Handle if user tries to open a badge with other badge already opened

        if (this.userIdSignal() && this.userIdSignal() !== value.badgeUserId) {
          this.badgeCardWasOpenSignal.set(false);
        } else if (this.userIdSignal() === value.badgeUserId) {
          // If the badge is already open, we should not set it to false
          this.badgeCardWasOpenSignal.set(true);
        }
        this.userIdSignal.set(value.badgeUserId);
        this.userOpenOwnBadgeSignal.set(this.userIdSignal() === this.mainPlayerIdSignal());

        const newBadgeData = value.badgeData.map((question) => ({
          ...question,
          value: question.multiValues ? question.multiValues.join(', ') : question.value,
        }));
        this.badgeQuestionsSignal.set(newBadgeData);

        if (this.userIdSignal() && !this.isPreviewCard() && !this.isSummaryView()) {
          try {
            const social = this.socialSignal();
            const userData = await this.getUser(this.userIdSignal()!, social?.socialId);
            this.userSignal.set(userData);
            this.baseUserSignal.set(userData as unknown as BaseUser);
          } catch (e) {
            this.error('Failed to get user info for baseUserSignal', e);
            this.baseUserSignal.set(undefined);
          }
        }

        void this.handleActionButtons();
        this.ringColorSignal.set(this.playerSignal()?.ringColor ?? value.badgeUserRingColor);
      });

    this.favoriteService.favoriteResultStream$.pipe(skip(1), takeUntil(this.destroyed$)).subscribe((resp) => {
      if (resp.userId && resp.userId !== '') {
        this.treatFavoriteResponse(resp);
      }
    });

    this.resetDragPosition();
  }

  private async handleActionButtons(): Promise<void> {
    if (this.isOwnBadgeSignal()) {
      this.blockActionButtonsSignal.set(false);
      this.blockMapButtonSignal.set(false);
      this.isOnPresentationSignal.set(false);
      this.isCrossRoomBadgeSignal.set(false);
    } else {
      let onPresentation = false;

      if (this.isUserPresentingToGroupSignal() || this.isUserPresentingToRoomSignal()) {
        onPresentation = true;
      } else {
        const gallery = await firstValueFrom(
          this.store.select(socialAppSelectors.getGallery).pipe(filter(notNil), takeUntil(this.destroyed$)),
        );
        const mainPlayerId = this.mainPlayerIdSignal();
        const isUserInGallery = gallery.some((g) => g.userId === mainPlayerId);
        if (isUserInGallery) {
          onPresentation = true;
        }
      }
      this.isOnPresentationSignal.set(onPresentation);

      if (this.badgingService.isSelectedInViewport) {
        this.isOnViewportSignal.set(true);
        this.blockActionButtonsSignal.set(true);
      } else {
        this.isOnViewportSignal.set(false);
        this.blockActionButtonsSignal.set(onPresentation);
      }

      const currentSocial = this.socialSignal();
      const targetBaseUser = this.baseUserSignal();

      if (targetBaseUser && currentSocial?.socialId !== targetBaseUser.socialId) {
        this.isCrossRoomBadgeSignal.set(true);
        this.blockMapButtonSignal.set(true);

        if (targetBaseUser.socialId) {
          this.checkIfCanJoinSocial(targetBaseUser.socialId);

          const connectedSet = await firstValueFrom(
            this.store
              .select(socialAppSelectors.getCurrentConnectedRoomSet)
              .pipe(filter(notNil), takeUntil(this.destroyed$)),
          );
          this.connectedRoomSetSignal.set(connectedSet.socials ?? []);
          const connectedRoomInfo = this.connectedRoomSetSignal()?.find(
            (room) => room.socialId === targetBaseUser.socialId,
          );
          this.roomNameSignal.set(connectedRoomInfo?.name);
          this.roomIdSignal.set(connectedRoomInfo?.socialId);
        }
        this.blockActionButtonsSignal.set(true);
      } else {
        this.isCrossRoomBadgeSignal.set(false);
        this.blockMapButtonSignal.set(false);
        if (!this.badgingService.isSelectedInViewport) {
          this.userReachableSignal.set(true);
        }
      }
    }
  }

  private getQuestionProperty(property: string): BadgeDataValue | undefined {
    return this.badgeQuestionsSignal()?.find((question) => question.propertyName === property);
  }

  private getBadgeTitle(): string | undefined {
    // Handle preview badge scenario
    if (this.isPreviewCard() || this.isSummaryView()) {
      return this.getQuestionProperty('Title')?.value ?? this.previewBadgeInfo()?.userTitle;
    }
    // For summary and regular badge, rely on badgeQuestionsSignal
    return this.getQuestionProperty('Title')?.value;
  }

  private getCompanyName(): string | undefined {
    // Handle preview badge scenario
    if (this.isPreviewCard() || this.isSummaryView()) {
      return this.getQuestionProperty('Company')?.value ?? this.previewBadgeInfo()?.companyName;
    }
    // For summary and regular badge, rely on badgeQuestionsSignal
    return this.getQuestionProperty('Company')?.value;
  }

  private propertyHasValue(propertyName: string): boolean {
    const prop = this.getQuestionProperty(propertyName);
    return !!prop?.value;
  }

  public get disableDrag(): boolean | undefined {
    return this.isOnboardingExperience() ? false : this.isPreviewCard() || this.customStyle() || this.summaryTimeline();
  }

  public shouldShowQuestion(question: BadgeDataValue): boolean {
    return question && question.value !== undefined && question.value !== '';
  }

  public async toggleChat(): Promise<void> {
    this.chatEngagedSignal.set(true);
    const userId = this.userIdSignal();
    if (userId) {
      //Trigger private message. If is from chat badge, don't close chat.
      this.chatService.toggleTriggerPrivateMessageBadge(userId, this.isBadgeChatSignal());
      //If chat was toggled from chat badge, close the badge
      if (this.isBadgeChatSignal()) {
        this.closeBadge();
        return;
      }
    }
    await delayFor(1500);
    this.chatEngagedSignal.set(false);
  }

  public onClickSocial(buttonSelected: string, url: string): void {
    if (url && url !== '') {
      this.goToLink(url);
    }
  }

  public goToLink(url: string): void {
    let urlToOpen = '';
    if (!/^http[s]?:\/\//.test(url)) {
      urlToOpen += 'http://';
    }
    urlToOpen += url;
    window.open(urlToOpen, '_blank');
  }

  public openSmartBadgingArticle(): void {
    const helpCenter = HelpCenterUrls.SMART_BADGING;
    window.open(helpCenter, '_blank');
  }

  public closeBadge(): void {
    if (this.isSummaryView() || this.summaryTimeline()) {
      this.summaryBadgeClosed.emit(true);
    } else {
      this.store.dispatch(socialAppActions.TOGGLE_BADGE_OPEN({ open: false, userId: '', ringColor: '' }));
    }
  }

  public ngOnDestroy(): void {
    this.userIdSignal.set(undefined);
    this.baseUserSignal.set(undefined);
    this.badgeQuestionsSignal.set(undefined); // Set to undefined or [] as appropriate
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private async getUser(userId: string, socialId?: string): Promise<User> {
    return await firstValueFrom(this.qaService.getUser(userId, socialId));
  }

  private formatName(firstName?: string, lastName?: string): string {
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    return firstName || lastName || '';
  }

  public teleportToUser(): void {
    if (this.userReachableSignal()) {
      this.teleportEngagedSignal.set(true);
      this.teleportService
        .teleportFlow({
          userId: this.userIdSignal() as string,
          ringColor: this.ringColorSignal() as string,
          mainPlayer: this.mainPlayerSignal(),
          toTeleportPlayer: this.playerSignal(),
          isCrossRoomTeleport: this.isCrossRoomBadgeSignal(),
          toTeleportRoomId: this.roomIdSignal(),
        })
        .catch((e) => {
          this.error('Failed to teleport user to other participant', e);
        })
        .finally(() => {
          this.teleportEngagedSignal.set(false);
        });
    }
  }

  public checkIfCanJoinSocial(socialId: string): void {
    if (!socialId) {
      this.userReachableSignal.set(false);
      this.blockActionMessageSignal.set(
        `Cannot determine room for ${this.formatName(this.baseUserSignal()?.firstName, this.baseUserSignal()?.lastName)}.`,
      );
      return;
    }
    this.videoChatService
      .canJoinSocial(socialId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((canJoin) => {
        this.userReachableSignal.set(canJoin.valueOf());
        const baseUser = this.baseUserSignal();
        if (!this.userReachableSignal()) {
          this.blockActionMessageSignal.set(
            `${this.formatName(
              baseUser?.firstName,
              baseUser?.lastName,
            )} is in ${this.checkRoomName()}. You can't join the room at the moment. Please try again later.`,
          );
        } else {
          if (this.isCrossRoomBadgeSignal()) {
            this.blockActionMessageSignal.set(
              `${this.formatName(
                baseUser?.firstName,
                baseUser?.lastName,
              )} is in ${this.checkRoomName()}. Change room on map dropdown to navigate to that room.`,
            );
          } else {
            this.blockActionMessageSignal.set('');
          }
        }
      });
  }

  public async findUserOnMap(): Promise<void> {
    this.findEngagedSignal.set(true);
    this.store.dispatch(socialAppActions.TOGGLE_SEARCH_PARTICIPANTS({ open: false }));
    const userId = this.userIdSignal();
    if (userId) {
      this.store.dispatch(socialAppActions.TOGGLE_MAP_OPEN({ open: true, userId }));
    }
    await delayFor(1500);
    this.findEngagedSignal.set(false);
  }

  private getBadgeHeight(): number {
    const badgeEl = document.getElementById('user-badge');
    return badgeEl?.offsetHeight ?? 270; // Fallback to default height
  }

  private getBadgeWidth(): number {
    const badgeEl = document.getElementById('user-badge');
    return badgeEl?.offsetWidth ?? 240; // Fallback to default width
  }

  private adjustBadgePositionToX(x: number): number {
    const badgeWidth = this.getBadgeWidth(); // Get the badge width
    const screenWidth = window.innerWidth;
    const centerX = screenWidth / 2;
    const offset = 30;
    const xNumber = typeof x === 'number' ? x : Number(x);
    // Positions badge to the left of the x position
    const newX = xNumber - badgeWidth - offset - centerX;

    return newX;
  }

  private adjustBadgePositionToY(y: number): number {
    const badgeHeight = this.getBadgeHeight();
    const screenHeight = window.innerHeight;
    const centerY = screenHeight / 2;
    const margin = 10;

    // Guarantee y is a number
    const yNumber = typeof y === 'number' ? y : Number(y);

    // Calculate new Y position relative to the center of the screen
    let newY = yNumber - centerY;

    // Verify if the badge is going to overflow the screen height
    const badgeBottom = yNumber + badgeHeight;

    if (badgeBottom + margin > screenHeight) {
      // Reposition the badge to avoid overflow
      const overflow = badgeBottom + margin - screenHeight;
      newY -= overflow;
    }
    return newY;
  }

  // Reset position of badge after being dragged
  private resetDragPosition(): void {
    const position = this.badgingService.positionToOpenBadge;
    if (position) {
      this.dragPosition = {
        x: Number(this.adjustBadgePositionToX(position.x)),
        y: Number(this.adjustBadgePositionToY(position.y)),
      };
    } else {
      this.dragPosition = { x: this.dragPosition.x, y: this.dragPosition.y };
    }
  }

  public featureChipMaxWidth(text: string): boolean {
    return text?.length >= this.featureChipMaxLength;
  }

  private getPreviewBadgeSubtitle(): string {
    const previewBadgeInfo = this.previewBadgeInfo();
    const title = previewBadgeInfo?.userTitle;
    const company = previewBadgeInfo?.companyName;
    if (title && company) {
      return `${title} @ ${company}`;
    }
    return title || company || '';
  }

  protected handleShowMoreLessBtn(): void {
    this.showBtnSignal.update((value) => !value);
  }

  private checkRoomName(): string {
    const roomName = this.roomNameSignal();
    if (!roomName) {
      return 'another room';
    }
    return roomName.toUpperCase().endsWith('ROOM') ? `${roomName}` : `${roomName} Room`;
  }

  public shouldShowIndicator(value: string): boolean {
    return value === this.visualIndicatorQuestionSignal();
  }

  private openFavoriteNotification(message: string, type?: SnackBarType, icon?: string, duration = 2000): void {
    const _data: SnackBarData = {
      message: message,
      type: type || '',
      icon: icon || '',
      mode: this.presentationService.isPresentationActive ? 'dark' : 'light',
    };
    this._snackBar.openFromComponent(SnackBarComponent, {
      duration: duration,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'custom-snackbar',
      data: _data,
    });
  }

  private treatFavoriteResponse(favoriteResponse: FavoriteResponse): void {
    this.favoriteResponseSignal.set(favoriteResponse);
    if (favoriteResponse.userId && favoriteResponse.userId !== '') {
      if (favoriteResponse.error) {
        this.openFavoriteNotification('Error: ' + favoriteResponse.error, 'error');
      } else {
        const currentUserName = this.userName();
        if (favoriteResponse.add) {
          this.openFavoriteNotification(
            `${currentUserName} has been added to your favorites`,
            'success',
            'FAVORITE_STAR_FILL_SUCCESS',
          );
        } else {
          this.openFavoriteNotification(
            `${currentUserName} has been removed from your favorites`,
            'success',
            'FAVORITE_STAR_FILL_SUCCESS',
          );
        }
      }
    }
  }

  protected openEditBadge(): void {
    this.store.dispatch(socialAppActions.UPDATE_BADGING_MODAL({ open: true }));
    this.closeBadge();
  }

  public clickedFavoriteIcon(): void {
    const userId = this.userIdSignal();
    const mainPlayerId = this.mainPlayerIdSignal();
    if (userId && mainPlayerId) {
      if (this.isFavoriteSignal()) {
        this.favoriteService.unfavoriteParticipant(mainPlayerId, userId);
      } else {
        this.favoriteService.favoriteParticipant(mainPlayerId, userId);
      }
    } else {
      this.error('Cannot favorite/unfavorite: userId or mainPlayerId is missing.');
    }
  }
}
